<template>
  <div
    v-if="chartDataQuery.loading.value"
    class="_loading-container"
  >
    <m-spinner />
  </div>
  <div
    v-else-if="chartDataQuery.error.value !== null"
    class="_alert"
  >
    <m-alert
      type="error"
      :message="chartDataQuery.error.value"
    />
  </div>
  <MetricDisplay
    v-else-if="chart.chartType === gridPageChartType.metric"
    :value="metricResult"
    :unit="unit"
    :loading="chartDataQuery.loading.value"
    :size="size"
  />
  <GridPageChartDataTable
    v-else-if="chart.chartType === gridPageChartType.table"
    :chart="chart"
    :chart-data-query="chartDataQuery"
  />
  <chart
    v-else
    :key="chart.chartType"
    :options="chartOptions"
    class="_chart"
  />
</template>

<script setup>
import GridPageChartDataTable from '@/components/custom-grid/GridPageChartDataTable.vue';
import MetricDisplay from '@/components/custom-grid/chart/MetricDisplay.vue';
import useBarChart from '@/composables/grid-page/chart/bar-chart';
import useChartDisplay from '@/composables/chart-display';
import useLineChart from '@/composables/grid-page/chart/line-chart';
import usePieChart from '@/composables/grid-page/chart/pie-chart';
import { Chart } from 'highcharts-vue';
import { computed, toRef } from 'vue';
import { gridPageChartType } from 'shared/constants.json';

const props = defineProps({
  chart: { type: Object, required: true },
  size: { type: String, default: 'medium', validator: (value) => ['small', 'medium', 'large'].includes(value) },
  chartDataQuery: { type: Object, required: true },
});

const { chartOptions: pieChartOptions } = usePieChart(props.chartDataQuery.result, toRef(props, 'chart'));
const { chartOptions: lineChartOptions } = useLineChart(props.chartDataQuery.result, toRef(props, 'chart'));
const { chartOptions: barChartOptions } = useBarChart(props.chartDataQuery.result, toRef(props, 'chart'));

const chartOptions = computed(() => {
  const baseOptions = { chart: { animation: false } };

  switch (props.chart.chartType) {
    case gridPageChartType.column:
    case gridPageChartType.bar:
      return barChartOptions.value;
    case gridPageChartType.line:
      return lineChartOptions.value;
    case gridPageChartType.pie:
      return pieChartOptions.value;
    default:
      return baseOptions;
  }
});

const metricResult = computed(() => {
  if (props.chart.chartType !== gridPageChartType.metric || props.chartDataQuery.result.value === null) {
    return null;
  }

  return Number(props.chartDataQuery.result.value.toFixed(2));
});

const { metricUnit } = useChartDisplay();

const unit = computed(() => metricUnit(props.chart.aggregationConfig.aggregation, props.chart.source));

</script>

<style scoped lang="scss">
._alert {
  width: 100%;
}

._chart {
  width: 100%;
  height: 100%;
}

._loading-container {
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
